/**
 * Exact-timestamp lookup into the loaded candle series.
 *
 * Pulled out of the overlay that needed it because the interesting behaviour is
 * the MISS: a caller asking for a bar that was never loaded must get `null`, not
 * the neighbouring bar. An off-by-one here would print one candle's OHLC beside
 * another candle's verdict — coherent-looking and quietly wrong, the same trap
 * the crosshair lookup in ChartView documents.
 *
 * Binary search rather than a Map index. The candle array is replaced on
 * essentially every tick, so a Map would be rebuilt O(n) per tick to answer one
 * question; the search is O(log n) per lookup and holds no state that can go
 * stale against the array it was built from.
 *
 * Assumes ascending `timestamp`, which is how the store keeps the series (the
 * chart requires it). No sortedness check: it would cost the O(n) scan the
 * search exists to avoid.
 */

/**
 * Index of the first bar at or after `timestamp`, or `candles.length` when the
 * timestamp is past the newest bar.
 *
 * This is the one search in the module; {@link findBarAt} is a thin exact-match
 * wrapper over it. The two callers genuinely want different answers — an OHLC
 * readout must refuse a near miss, while "jump to this date" must land on the
 * next bar when the picked instant falls in a session gap — but there is no
 * reason for two implementations of the same lower-bound scan to drift apart.
 *
 * Returns an INDEX rather than a bar because the chart positions itself with
 * logical indices (see `chartEdges.ts`): index 0 is the oldest loaded bar, so
 * an index is directly usable as a scroll target and a bar object is not.
 */
export function findBarIndexAtOrAfter<T extends { timestamp: number }>(
  candles: readonly T[] | null | undefined,
  timestamp: number
): number {
  if (!candles || candles.length === 0) return 0;

  let lo = 0;
  let hi = candles.length; // exclusive; ending here means "past the newest bar"

  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (candles[mid].timestamp < timestamp) lo = mid + 1;
    else hi = mid;
  }

  return lo;
}

export function findBarAt<T extends { timestamp: number }>(
  candles: readonly T[] | null | undefined,
  timestamp: number
): T | null {
  if (!candles || candles.length === 0) return null;

  const index = findBarIndexAtOrAfter(candles, timestamp);

  // Between two bars, before the first, or after the last — all the same
  // answer. The caller renders nothing rather than the closest bar.
  if (index >= candles.length) return null;
  return candles[index].timestamp === timestamp ? candles[index] : null;
}
